import { Button, Col, Divider, Form, Input, Modal, Row, message, DatePicker, TimePicker } from "antd";
import { getCookie } from "../../../../helpers/localStorage";
import { FacilityID, Role } from "../../../../constants/defaultKeys";
import { createProgressNoteAPI, updateProgressNoteAPI } from "../../../../services/outpatient.services";
import dayjs from "dayjs";
import { useEffect } from "react";
import utc from "dayjs/plugin/utc";
import useIsMobile from "../../../../hooks/useIsMobile";
import styles from "./AddNotesOp.module.css";
import { alignPickerPopupBelow } from "../../../../helpers/alignPickerPopup";
import "../../../PatientsIP/ProgressMonitoring/ProgressNotesPicker.css";
dayjs.extend(utc);

const { TextArea } = Input;

function AddNotesOp({
  admissionId,
  form,
  getData,
  isModalOpen,
  setIsModalOpen,
  userDetails,
}) {
  const userRole = getCookie(Role) || userDetails?.currRole;
  const facilityId = Number(getCookie(FacilityID)) || userDetails?.currFacility;
  const isMobile = useIsMobile();
  const isCompactPicker = useIsMobile(1200);

  useEffect(() => {
    if (!isModalOpen?.status) return;

    if (isModalOpen?.type === "add") {
      if (isCompactPicker) {
        form.setFieldsValue({
          record_date: dayjs(),
          record_time_only: dayjs(),
          record_time: undefined,
        });
      } else {
        form.setFieldsValue({
          record_time: dayjs(),
        });
      }
    }
  }, [isModalOpen, isCompactPicker, form]);

  async function onFinish(values) {
    try {
      let recordTime;

      if (isCompactPicker) {
        recordTime = dayjs(values.record_date)
          .hour(dayjs(values.record_time_only).hour())
          .minute(dayjs(values.record_time_only).minute())
          .second(0)
          .utc()
          .format("YYYY-MM-DD HH:mm:ss");
      } else {
        recordTime = values.record_time.utc().format("YYYY-MM-DD HH:mm:ss");
      }

      const payload = {
        admission: admissionId,
        ...values,
        record_time: recordTime,
      };

      let res;
      let successMessage = "";

      if (isModalOpen?.type === "add") {
        res = await createProgressNoteAPI(payload);
        successMessage = "Progress Notes created successfully";
      } else {
        res = await updateProgressNoteAPI(isModalOpen?.id, payload);
        successMessage = "Progress Notes updated successfully";
      }

      message.success(res?.data?.message || successMessage);
      setIsModalOpen({ status: false, type: "add", id: 0 });
      getData();
    } catch (error) {
      message.error(error?.response?.data?.message || "Something went wrong");
    }
  }

  return (
    <Modal
      footer={null}
      title={isModalOpen?.type === "add" ? "Add Notes" : "Update Notes"}
      open={isModalOpen?.status}
      onCancel={() => setIsModalOpen({ status: false, type: "add", id: 0 })}
      centered={!isMobile}
      width={isMobile ? "92%" : 600}
      wrapClassName={isMobile ? styles.addNotesOpModalWrap : undefined}
      destroyOnClose
    >
      <Form
        form={form}
        name="notes"
        layout="vertical"
        onFinish={onFinish}
        className={isMobile ? styles.addNotesOpFormMobile : undefined}
      >
        <Divider />
        <Row style={isMobile ? undefined : { marginTop: "18px" }}>
          <Col span={24}>
            <Form.Item
              label="Progress Notes"
              name="notes"
              rules={[{ required: true, message: "Please input progress notes!" }]}
            >
              <TextArea
                style={{ minHeight: "100px" }}
                placeholder="Add Progress Notes"
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label="Record Time" required>
              {isCompactPicker ? (
                <Row gutter={[8, 8]}>
                  <Col span={24}>
                    <Form.Item
                      name="record_date"
                      rules={[{ required: true, message: "Select date" }]}
                      noStyle
                    >
                      <DatePicker
                        style={{ width: "100%" }}
                        format="YYYY-MM-DD"
                        placement="bottomLeft"
                        getPopupContainer={() => document.body}
                        popupClassName="curengo-mobile-picker"
                        inputReadOnly
                        onOpenChange={(open) => {
                          alignPickerPopupBelow(
                            open,
                            ".curengo-mobile-picker.ant-picker-dropdown"
                          );
                        }}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={24}>
                    <Form.Item
                      name="record_time_only"
                      rules={[{ required: true, message: "Select time" }]}
                      noStyle
                    >
                      <TimePicker
                        style={{ width: "100%" }}
                        format="HH:mm"
                        placement="bottomLeft"
                        getPopupContainer={() => document.body}
                        popupClassName="progress-notes-time-picker"
                        inputReadOnly
                        onOpenChange={(open) => {
                          alignPickerPopupBelow(
                            open,
                            ".progress-notes-time-picker.ant-picker-dropdown",
                            { offsetUp: 48 }
                          );
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              ) : (
                <Form.Item
                  name="record_time"
                  rules={[{ required: true, message: "Please select record time" }]}
                  noStyle
                >
                  <DatePicker
                    showTime={{ format: "HH:mm" }}
                    format="YYYY-MM-DD HH:mm"
                    style={{ width: "100%" }}
                    getPopupContainer={() => document.body}
                    popupClassName="progress-notes-record-time-picker"
                    onOpenChange={(open) => {
                      alignPickerPopupBelow(
                        open,
                        ".progress-notes-record-time-picker.ant-picker-dropdown",
                        { offsetUp: 72 }
                      );
                    }}
                  />
                </Form.Item>
              )}
            </Form.Item>
          </Col>

          <Divider />
          <Col span={24} className={isMobile ? styles.submitRow : "flex-end"}>
            <Form.Item wrapperCol={isMobile ? undefined : { span: 4 }}>
              <Button className="inv-btn" type="primary" htmlType="submit">
                {isModalOpen?.type === "add" ? "Add" : "Update"}
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}

export default AddNotesOp;
